import { useEffect, useMemo, useRef } from "react";
import EntityMediaCarousel from "../../entity-media-carousel/EntityMediaCarousel";
import { useMediaActions, useMediaSelector } from "../../../store/media";
import { useSelectionSelector } from "../../../store/selection";
import { useAuthSelector } from "../../../store/auth";
import { fetchMediaUrlAndThumbnail, updateMediaList } from "../../media/MediaDrawer";
import { useSpinner } from "../../../contexts/SpinnerContext";

import type { MediaItemDetailed } from "../../../types";
import { EntityMediaModal } from "./MediaEntityMapModal";

function ModalContent() {
  const { items, selectedItem } = useMediaSelector();
  const { setSelectedItem, setMediaItems } = useMediaActions();
  const { user } = useAuthSelector();
  const { startLoading, stopLoading } = useSpinner();

  const currentId = useMemo(
    () => items?.findIndex((item) => `${item.type}-${item.id}` === `${selectedItem?.type}-${selectedItem?.id}`) || 0,
    [selectedItem, items]
  );

  if (!items || !items.length || !selectedItem) return null;

  async function handleChangeSlide(index: number, item?: MediaItemDetailed) {
    const selectedItem = item || items?.[index];
    if (!selectedItem) return;
    // Fetch complete media details and update store list
    try {
      startLoading();

      const previewItem = await fetchMediaUrlAndThumbnail(selectedItem, user);

      // If list item is already updated with up-to-date media details skip list update
      // otherwise update media list with `previewItem`
      if (item?.url !== previewItem?.url) {
        const updatedList = updateMediaList(items || [], previewItem as MediaItemDetailed) || [];
        setMediaItems(updatedList);
      }

      setSelectedItem(previewItem as MediaItemDetailed);
    } catch (err) {
      setSelectedItem(selectedItem);
    } finally {
      stopLoading();
    }
  }

  return <EntityMediaCarousel items={items || []} currentItem={selectedItem} index={currentId} onChange={handleChangeSlide} />;
}

export default function MediaEntityDetailModal() {
  const prevSelectedEntityRef = useRef<string | null>(null);

  const { setCommonMediaState, closeMediaDetail } = useMediaActions();
  const { isMediaDetailOpen, selectedItem } = useMediaSelector();

  // Hide the outer ModalControl X when an embed is showing — EmbedRenderer
  // renders its own collapsible close button that sits above the iframe.
  // Check media_type first (most reliable), then fall back to embed_url presence.
  const isEmbed =
    selectedItem?.media_type?.startsWith("embed") ||
    Boolean(selectedItem?.embed_url || (selectedItem as any)?.remote_details?.embed_url) ||
    false;

  const { selectedEntityId } = useSelectionSelector();

  // Cleanup media state when the selected entity changes.
  // Using selectedEntityId from the Zustand store instead of useSelectedEntity()
  // to avoid subscribing to React Query and triggering background refetches.
  useEffect(() => {
    if (!selectedEntityId) {
      prevSelectedEntityRef.current = null;
      return;
    }

    const key = String(selectedEntityId);
    if (!prevSelectedEntityRef.current) {
      prevSelectedEntityRef.current = key;
    } else {
      if (prevSelectedEntityRef.current !== key) {
        prevSelectedEntityRef.current = key;
        setCommonMediaState({ selectedItem: undefined, items: [] });
      }
    }
  }, [selectedEntityId, setCommonMediaState]);

  // Close Layer B overlay on unmount.
  useEffect(() => {
    return () => {
      closeMediaDetail();
    };
  }, [closeMediaDetail]);

  return (
    <EntityMediaModal
      open={isMediaDetailOpen}
      showContent={true}
      onClose={closeMediaDetail}
      contentSx={{ maxHeight: "inherit" }}
      containerSx={{ zIndex: 9999 }}
      hideToggleButton={true}
      hideControl={isEmbed}
    >
      <ModalContent />
    </EntityMediaModal>
  );
}
